import { z, ZodType } from "zod";

/**
 * A geographic bounding box defined by min/max latitude and longitude values.
 * Used to define regions where location anonymisation precision can be overridden.
 */
export type GeoBoundsUnbranded = {
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
};

export const GeoBoundsSchema = z
  .object({
    minLatitude: z.number().min(-90).max(90),
    maxLatitude: z.number().min(-90).max(90),
    minLongitude: z.number().min(-180).max(180),
    maxLongitude: z.number().min(-180).max(180),
  })
  .refine((data) => data.minLatitude < data.maxLatitude, {
    message: "minLatitude must be less than maxLatitude",
  })
  .refine((data) => data.minLongitude < data.maxLongitude, {
    message: "minLongitude must be less than maxLongitude",
  })
  .brand<"vision-common:GeoBounds">() satisfies ZodType<GeoBoundsUnbranded>;

export type GeoBounds = z.infer<typeof GeoBoundsSchema>;

/**
 * A location precision override allows customising the anonymisation precision
 * for specific geographic areas. Urban areas typically require finer precision
 * (e.g., 0.01) compared to the default (0.05) to maintain useful location data
 * while preserving anonymity.
 */
export type LocationPrecisionOverrideUnbranded = {
  id: string;
  name: string;
  bounds: GeoBoundsUnbranded;
  /** The precision value used for rounding lat/long coordinates. Smaller values = finer precision. */
  precision: number;
  createdAt: Date;
  updatedAt: Date;
};

export const LocationPrecisionOverrideSchema = z
  .object({
    id: z.uuid(),
    name: z.string().min(1).max(255),
    bounds: GeoBoundsSchema,
    precision: z.number().positive().max(1),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .brand<"vision-common:LocationPrecisionOverride">();

export type LocationPrecisionOverride = z.infer<
  typeof LocationPrecisionOverrideSchema
>;

/**
 * Request type for creating or updating a location precision override.
 */
export type CreateLocationPrecisionOverrideRequestUnbranded = {
  name: string;
  bounds: GeoBoundsUnbranded;
  precision: number;
};

export const CreateLocationPrecisionOverrideRequestSchema = z.object({
  name: z.string().min(1).max(255),
  bounds: GeoBoundsSchema,
  precision: z.number().positive().max(1),
});

export type CreateLocationPrecisionOverrideRequest = z.infer<
  typeof CreateLocationPrecisionOverrideRequestSchema
>;

export type UpdateLocationPrecisionOverrideRequestUnbranded = {
  name?: string;
  bounds?: GeoBoundsUnbranded;
  precision?: number;
};

export const UpdateLocationPrecisionOverrideRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  bounds: GeoBoundsSchema.optional(),
  precision: z.number().positive().max(1).optional(),
});

export type UpdateLocationPrecisionOverrideRequest = z.infer<
  typeof UpdateLocationPrecisionOverrideRequestSchema
>;

/**
 * Type representing an anonymised geo location that may include
 * information about which precision override was applied.
 */
export type AnonymisedGeoLocation = {
  latitude: number;
  longitude: number;
  precision: number;
  /** ID of the precision override that was applied, if any */
  precisionOverrideId?: string;
};

/**
 * Checks if a geographic point falls within a bounding box.
 */
export function isPointInBounds(
  latitude: number,
  longitude: number,
  bounds: GeoBoundsUnbranded,
): boolean {
  return (
    latitude >= bounds.minLatitude &&
    latitude <= bounds.maxLatitude &&
    longitude >= bounds.minLongitude &&
    longitude <= bounds.maxLongitude
  );
}

/**
 * Finds the most precise (smallest precision value) override that contains the given point.
 * Returns undefined if no override contains the point.
 */
export function findMostPreciseOverride(
  latitude: number,
  longitude: number,
  overrides: LocationPrecisionOverrideUnbranded[],
): LocationPrecisionOverrideUnbranded | undefined {
  const matchingOverrides = overrides.filter((override) =>
    isPointInBounds(latitude, longitude, override.bounds),
  );

  if (matchingOverrides.length === 0) {
    return undefined;
  }

  // Return the one with smallest precision value (most precise)
  return matchingOverrides.reduce((mostPrecise, current) =>
    current.precision < mostPrecise.precision ? current : mostPrecise,
  );
}

/**
 * Default precision used when no override applies.
 */
export const DEFAULT_LOCATION_PRECISION = 0.05;
